import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Loader2, Trash2 } from 'lucide-react';
import { AccrualsTableProps } from '../types';
import { Accrual } from '../../../api/cashflow';

/**
 * Премиум покупателя: подписка активна, если флаг стоит и дата не прошла.
 * Начисление её не выдаёт — метка нужна, чтобы заметить, кому забыли выдать.
 */
const premiumLabel = (
  user: Accrual['user'],
): { text: string; tone: 'ok' | 'warn' | 'none' } => {
  if (!user) return { text: '—', tone: 'none' };
  const until = user.subscribedEnd ? new Date(user.subscribedEnd) : null;
  if (!user.isSubscribed || !until || until <= new Date()) {
    return { text: 'нет премиума', tone: 'warn' };
  }
  return { text: `до ${until.toLocaleDateString('ru-RU')}`, tone: 'ok' };
};

export function AccrualsTable({
  isDark,
  accruals,
  isLoading,
  onEdit,
  onDelete,
  formatMoney,
}: AccrualsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const th = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');
  const rowCls = cn(
    'transition-colors',
    isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
  );

  return (
    <Table>
      <TableHeader>
        <TableRow className={rowCls}>
          <TableHead className={th}>Дата продажи</TableHead>
          <TableHead className={th}>Провайдер</TableHead>
          <TableHead className={th}>Сумма</TableHead>
          <TableHead className={th}>В сумах</TableHead>
          <TableHead className={th}>Покупатель</TableHead>
          <TableHead className={th}>Премиум</TableHead>
          <TableHead className={th}>Комментарий</TableHead>
          <TableHead className={th}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {accruals.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-12 text-slate-500">
              Начислений за период нет
            </TableCell>
          </TableRow>
        ) : (
          accruals.map((accrual) => {
            const premium = premiumLabel(accrual.user);
            return (
              <TableRow key={accrual.id} className={rowCls}>
                <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                  {accrual.date}
                </TableCell>
                <TableCell>
                  <Badge className="rounded-lg font-medium border-0 bg-[#C9A227]/10 text-[#C9A227]">
                    {accrual.provider?.name || '—'}
                  </Badge>
                </TableCell>
                <TableCell
                  className={cn(
                    'font-semibold',
                    accrual.amount < 0
                      ? 'text-red-500'
                      : isDark
                        ? 'text-white'
                        : 'text-[#111111]',
                  )}
                >
                  {formatMoney(accrual.amount, accrual.currency)}
                </TableCell>
                <TableCell className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
                  {formatMoney(accrual.amount * accrual.rate, 'UZS')}
                </TableCell>
                <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                  {accrual.user ? accrual.user.username : '—'}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'text-xs px-2 py-1 rounded-lg',
                      premium.tone === 'ok' && 'bg-emerald-500/10 text-emerald-500',
                      premium.tone === 'warn' && 'bg-amber-500/10 text-amber-500',
                      premium.tone === 'none' && (isDark ? 'text-slate-500' : 'text-slate-400'),
                    )}
                  >
                    {premium.text}
                  </span>
                </TableCell>
                <TableCell className={cn('max-w-xs truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  {accrual.comment || '—'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(accrual)}
                      className={cn('h-8 w-8', isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100')}
                    >
                      <Edit className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(accrual.id)}
                      className={cn('h-8 w-8', isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100')}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
